/**
 * MOS/AOC requirement profiles — E2-S2 portal admin CRUD.
 *
 * Consumes GET/POST/PUT/DELETE /mos-profiles from chamber-v2. A
 * course_director can create/edit profiles end-to-end without engineering,
 * satisfying the "profiles are data" accept criterion at the UI layer
 * (backend already satisfies it via API — this is the human-usable surface).
 */

import {
  ArrayField,
  ArrayInput,
  Create,
  Datagrid,
  Edit,
  FunctionField,
  List,
  NumberInput,
  Show,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  SelectInput,
  regex,
  required,
  minValue,
  maxValue,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Chip } from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { ArchivedSelect } from '../components/selects';
import { hasRole, type Permissions } from '../types';

import { TAG_PATTERN } from './mos-profiles-helpers';
export { TAG_PATTERN, validateRequirement } from './mos-profiles-helpers';

const KIND_CHOICES = [
  { id: 'MOS', name: 'MOS (enlisted)' },
  { id: 'AOC', name: 'AOC (officer)' },
];

const RequirementsCount = () => {
  const record = useRecordContext();
  const n = Array.isArray(record?.requirements) ? record.requirements.length : 0;
  return <span>{n}</span>;
};
RequirementsCount.displayName = 'RequirementsCount';

const KindChip = () => {
  const record = useRecordContext();
  if (!record?.kind) return null;
  return (
    <Chip
      label={record.kind}
      size="small"
      color={record.kind === 'AOC' ? 'primary' : 'default'}
      variant={record.kind === 'AOC' ? 'filled' : 'outlined'}
    />
  );
};
KindChip.displayName = 'KindChip';

export const MosProfileList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'code', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid rowClick={hasRole(permissions, 'course_director') ? 'edit' : 'show'}>
        <TextField source="code" />
        <TextField source="title" />
        <TextField source="branch" />
        <FunctionField label="Kind" render={() => <KindChip />} />
        <FunctionField label="# KSB reqs" render={() => <RequirementsCount />} />
      </Datagrid>
    </List>
  );
};

export const MosProfileShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="code" />
      <TextField source="title" />
      <TextField source="branch" />
      <TextField source="kind" />
      <TextField source="description" />
      <ArrayField source="requirements">
        <SingleFieldList linkType={false}>
          <FunctionField
            render={(record) =>
              record ? (
                <Chip
                  size="small"
                  label={`${record.tag} ≥ ${record.minPct}%`}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ) : null
            }
          />
        </SingleFieldList>
      </ArrayField>
    </SimpleShowLayout>
  </Show>
);

const MosProfileForm = () => (
  <SimpleForm toolbar={<ArchivalToolbar />}>
    <TextInput
      fullWidth
      source="code"
      helperText="MOS/AOC code — used as the resource id (e.g. 11B, 68W, 11A)"
      validate={[required(), regex(/^[A-Z0-9]{2,6}$/, '2–6 uppercase letters/digits')]}
    />
    <TextInput fullWidth source="title" validate={required()} />
    <TextInput fullWidth source="branch" helperText="Branch (e.g. Infantry, Medical, Signal)" />
    <SelectInput source="kind" choices={KIND_CHOICES} defaultValue="MOS" validate={required()} />
    <TextInput fullWidth multiline source="description" />

    <ArrayInput source="requirements" label="Critical KSB requirements">
      <SimpleFormIterator inline>
        <TextInput
          source="tag"
          label="KSB tag"
          helperText="e.g. S-PHYS, K-TCCC, B-LEAD"
          validate={[required(), regex(TAG_PATTERN, 'tag must match K-*, S-*, or B-*')]}
        />
        <NumberInput
          source="minPct"
          label="Min %"
          min={0}
          max={100}
          step={1}
          validate={[required(), minValue(0), maxValue(100)]}
          defaultValue={60}
        />
      </SimpleFormIterator>
    </ArrayInput>
  </SimpleForm>
);

export const MosProfileCreate = () => (
  <Create>
    <MosProfileForm />
  </Create>
);

export const MosProfileEdit = () => (
  <Edit mutationMode="pessimistic">
    <MosProfileForm />
  </Edit>
);

export const mos_profiles = {
  list: MosProfileList,
  show: MosProfileShow,
  create: MosProfileCreate,
  edit: MosProfileEdit,
};
